import { array } from '@ember/helper';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { once } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import $ from 'jquery';
import moment from 'moment';

import type { CustomPeriodPickerOptions, Service } from 'webapp-types';
import { TrackingEvent } from 'webapp/enums/tracking';
import { MD_BREAKPOINT, uniqueId } from 'webapp/utils/common';

import themed from 'webapp/decorators/themed';
import joinClasses from 'webapp/helpers/join-classes';
import didInsert from 'webapp/modifiers/did-insert';
import didUpdate from 'webapp/modifiers/did-update';
import willDestroy from 'webapp/modifiers/will-destroy';
import FormInputComponent from 'webapp/components/form/input';

export interface Args {
	format: string;
	startValue: string | null;
	endValue: string | null;
	isRolling: boolean;
	isRollingCheckboxDisabled: boolean;
	isRollingCheckboxHidden: boolean;
	extraClass?: string;
	isDisabled?: boolean;
	trackingOrigin?: string;
	customPeriodPickerOptions?: CustomPeriodPickerOptions;
	onChange: (startDate: string, endDate: string, isRolling: boolean) => void;
	onOk?: () => void;
	theme?: string;
	size?: string;
}

@themed()
export default class FormCustomPeriodPickerComponent extends Component<Args> {
	@service('account') declare accountService: Service.Account;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('navigation') declare navigationService: Service.Navigation;

	@tracked startValue!: string | null; // set in updateLocalValues
	@tracked endValue!: string | null; // set in updateLocalValues
	@tracked isRolling!: boolean;

	// from themed
	declare theme: string;
	declare themeClass: string;
	declare size: string;
	declare sizeClass: string;

	private picker: PeriodPicker | null = null;

	private formatInPicker: string;

	private maxSelectedDays = 3650; // 10 years

	private _pickerElInitialHeight: number | null = null; // initial height of normal sized picker

	startInputName: string;
	endInputName: string;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.formatInPicker = this.accountService.accountDateFormat;

		this.startInputName = `cpp-${uniqueId()}`;
		this.endInputName = `cpp-${uniqueId()}`;
		this.isRolling = this.args.isRolling || false;

		this.updateLocalValues();
	}

	get format() {
		return this.args.format;
	}

	get startValueFormatted() {
		return moment(this.startValue, this.format).format(this.formatInPicker);
	}

	set startValueFormatted(value) {
		const formatted = moment(value, this.formatInPicker).format(this.args.format);
		this.startValue = formatted;
		once(this, this.change);
	}

	get endValueFormatted() {
		return moment(this.endValue, this.format).format(this.formatInPicker);
	}

	set endValueFormatted(value) {
		const formatted = moment(value, this.formatInPicker).format(this.args.format);
		this.endValue = formatted;
		once(this, this.change);
	}

	@action
	updateLocalValues() {
		this.startValue = this.args.startValue;
		this.endValue = this.args.endValue;
	}

	@action
	setupPeriodpicker(node: HTMLElement) {
		const that = this,
			el = $(node),
			startEl: JQuery<HTMLInputElement> = el.find('.period-picker-start'),
			endEl: JQuery<HTMLInputElement> = el.find('.period-picker-end');

		if (!startEl || !endEl) {
			return;
		}

		const options = {
			end: endEl,
			placement: this.args.customPeriodPickerOptions?.placement ?? 'top',
			resizeButton: false,
			formatDate: this.formatInPicker,
			formatDecoreDate: this.accountService.accountDateFormatHumanReadableWithoutYear, // in the button
			formatDecoreDateWithYear: this.accountService.accountDateFormatHumanReadable, // in the button
			formatDecoreDateWithoutMonth:
				this.accountService.accountDateFormatHumanReadableWithoutYear, // in the button
			fullsize: false,
			fullsizeOnDblClick: false,
			draggable: false,
			timepicker: false,
			yearsLine: false,
			title: false,
			mousewheel: false,
			dayOfWeekStart: this.accountService.firstDayOfWeek || 7, // we have Mon - 1, Sun - 0, picker accepts Mon - 1, Sun - 7
			minDate: moment().subtract(10, 'year').format(this.formatInPicker),
			maxDate: moment().add(10, 'year').format(this.formatInPicker),
			cells: [1, 2],
			monthWidthInPixels: 240,
			monthHeightInPixels: this.args.isRollingCheckboxHidden ? 255 : 280, // for 5 week lines
			someYOffset: 70,
			fullYearsInFullscreen: true,
			onAfterRegenerate: function (this: { picker: PeriodPicker }) {
				// we pass this.picker (jquery el) to these fns
				// because picker obj is not set yet when these are called for the first time
				that.preparePickerPopupForMobile(this.picker);
				that.moveWeekdayNamesRow(this.picker);
				that.updateFirstAndLastSelected(this.picker);
			},
			onChange: function (this: { picker: PeriodPicker }) {
				const newStartValue = startEl.get(0)!.value;
				const newEndValue = endEl.get(0)!.value;
				const pickerFormat = that.formatInPicker;
				const startDate = moment(newStartValue, pickerFormat);
				const endDate = moment(newEndValue, pickerFormat);

				const validation = that.validateDateRange(startDate, endDate);
				const endInputEl = this.picker.find('.period_picker_to_block .input_box');

				const isRangeRejected = validation.isInvalid || validation.isTooManyDays;
				this.picker.find('.period_picker_ok').prop('disabled', isRangeRejected);

				if (isRangeRejected) {
					endInputEl.addClass('period_picker_error');

					let errorMsg = '';
					if (validation.isInvalid) {
						errorMsg = 'End date cannot be before start date.';
					} else if (validation.isTooManyDays) {
						errorMsg = `Maximum ${that.maxSelectedDays} days can be selected.`;
					}

					that.showErrorMessage(this.picker, errorMsg);
				} else {
					endInputEl.removeClass('period_picker_error');
					that.hideErrorMessage(this.picker);
				}

				if (validation.isInvalid) {
					return;
				}

				that.startValueFormatted = newStartValue;
				that.endValueFormatted = newEndValue;
				that.updatePickerDaysSelectedCount(this.picker);
				that.updateFirstAndLastSelected(this.picker);
				that.updateDateInputs(this.picker);
			},
			onAfterShow: function (this: { picker: PeriodPicker }) {
				that.preparePickerPopupForMobile(this.picker);
				that.updateFirstAndLastSelected(this.picker);
				that.updateRollingRangeCheckbox(this.picker);
				that.preventEnterOnRejectedRange(this.picker);
				this.picker.addClass(that.themeClass);
				that.analyticsService.event(TrackingEvent.CUSTOM_DATE_PERIOD_PICKER_OPENED, {
					origin: that.args.trackingOrigin,
				});

				// replace font icons with UiIcon for xdsoft calendar navigation arrows
				const xdsoftPrev = this.picker.find('.xdsoft_navigate_prev');
				if (xdsoftPrev.length && !xdsoftPrev.find('ui-icon').length) {
					xdsoftPrev.append('<ui-icon name="ChevronLeft" size="xs"></ui-icon>');
				}
				const xdsoftNext = this.picker.find('.xdsoft_navigate_next');
				if (xdsoftNext.length && !xdsoftNext.find('ui-icon').length) {
					xdsoftNext.append('<ui-icon name="ChevronRight" size="xs"></ui-icon>');
				}

				// replace font icon with UiIcon for period date separator (arrow between dates in popup)
				const periodDateSeparator = this.picker.find('.period_picker_date_separator');
				if (periodDateSeparator.length && !periodDateSeparator.find('ui-icon').length) {
					periodDateSeparator.append('<ui-icon name="ArrowRight" size="xs"></ui-icon>');
				}
			},

			onOkButtonClick: function () {
				const startDate = moment(startEl.get(0)!.value, that.formatInPicker);
				const endDate = moment(endEl.get(0)!.value, that.formatInPicker);

				const validation = that.validateDateRange(startDate, endDate);

				if (validation.isInvalid || validation.isTooManyDays) {
					return false;
				}

				if (that.args.onOk) {
					that.args.onOk();
				}
				return;
			},
		};

		startEl.periodpicker(options);

		const picker = startEl.periodpicker('picker');
		this.picker = picker;

		el.find('.period_picker_input').addClass(this.themeClass).addClass(this.sizeClass);
	}

	validateDateRange(startDate: moment.Moment, endDate: moment.Moment) {
		const daysDiff = endDate.diff(startDate, 'days') + 1;

		return {
			isInvalid: endDate.isBefore(startDate),
			isTooManyDays: this.maxSelectedDays && daysDiff > this.maxSelectedDays,
		};
	}

	// The periodpicker closes on Enter itself, bypassing the disabled OK button.
	preventEnterOnRejectedRange(pickerEl: PeriodPicker) {
		const dateInputs = pickerEl.find('.period_picker_submit_dates .period_picker_date input');
		if (dateInputs.attr('data-enter-guarded')) {
			return;
		}
		dateInputs.attr('data-enter-guarded', 'true');

		dateInputs.each((_, input) => {
			input.addEventListener(
				'keydown',
				(event) => {
					if (event.key !== 'Enter') {
						return;
					}

					const validation = this.validateDateRange(
						moment(dateInputs.eq(0).val(), this.formatInPicker),
						moment(dateInputs.eq(1).val(), this.formatInPicker),
					);
					if (validation.isInvalid || validation.isTooManyDays) {
						event.stopImmediatePropagation();
					}
				},
				true,
			);
		});
	}

	updatePickerDaysSelectedCount(pickerEl: PeriodPicker) {
		const bottomBox = pickerEl.find('.period_picker_submit_dates');

		let daysCt = bottomBox.find('.period_picker_days_selected');
		if (!daysCt.length) {
			daysCt = $('<span class="period_picker_days_selected"><b></b> selected</span>');
			bottomBox.find('.period_picker_ok').before(daysCt);
		}

		const startMoment = moment(this.startValue, this.args.format),
			endMoment = moment(this.endValue, this.args.format),
			diff = endMoment.diff(startMoment, 'days'),
			absoluteDiff = Math.abs(diff) + 1; // inclusive

		daysCt.find('b').text(`${absoluteDiff} day${absoluteDiff === 1 ? '' : 's'}`);
	}

	updateFirstAndLastSelected(pickerEl: PeriodPicker) {
		const startMoment = moment(this.startValue, this.args.format),
			endMoment = moment(this.endValue, this.args.format);

		const cells = pickerEl
			.find('.period_picker_cell')
			.removeClass('period_picker_selected_first period_picker_selected_last')
			.filter('.period_picker_selected');

		cells
			.filter(`[data-date="${startMoment.format(this.formatInPicker)}"]`)
			.addClass('period_picker_selected_first');
		cells
			.filter(`[data-date="${endMoment.format(this.formatInPicker)}"]`)
			.addClass('period_picker_selected_last');
	}

	updateRollingRangeCheckbox(pickerEl: PeriodPicker) {
		if (this.args.isRollingCheckboxHidden) {
			return;
		}

		const bottomBox = pickerEl.find('.period_picker_submit_dates');
		// in case .period_picker_is_rolling already exists just update
		// checkbox state according to state from outside
		if (bottomBox.find('.period_picker_is_rolling').length) {
			const isChecked = this.args.isRolling;
			$('#isRolling').prop('checked', isChecked);
			// update checkmark visibility
			const checkIcon = bottomBox.find('.period_picker_is_rolling .checkbox-check');
			if (isChecked) {
				checkIcon.show();
			} else {
				checkIcon.hide();
			}

			return;
		}

		const checked = this.args.isRolling ? 'checked' : '',
			disabled = this.args.isRollingCheckboxDisabled ? 'disabled' : '';

		// creating elements for .period_picker_is_rolling
		// checkbox with checkmark icon (matching Form::Checkbox component structure)
		const inputCheckboxEl = $(`<input id="isRolling" type="checkbox" ${checked} ${disabled}>`),
			checkmarkIconEl = $(
				'<ui-icon name="CheckboxTick" size="xs" class="checkbox-check"></ui-icon>',
			),
			checkboxLabelEl = $('<label for="isRolling"></label>').append(
				checkmarkIconEl,
				'Rolling range',
			);

		// show/hide checkmark based on initial state
		if (!this.args.isRolling) {
			checkmarkIconEl.hide();
		}

		inputCheckboxEl.on('change.isRolling', ({ currentTarget }) => {
			const isChecked = $(currentTarget).prop('checked') as boolean;
			this.isRolling = isChecked;
			// Toggle checkmark visibility
			if (isChecked) {
				checkmarkIconEl.show();
			} else {
				checkmarkIconEl.hide();
			}
			this.change();
		});

		const inputCheckboxWrapperEl = $(
			`<span class="checkbox theme-dark-blue2 size-small ${disabled}"></span>`,
		).append(inputCheckboxEl, checkboxLabelEl);

		// Tooltip with icon
		const tooltipIconEl = $('<ui-icon name="Question" size="md" />');

		tooltipIconEl.tooltipster({
			content: 'Dates adjust relative to current date.',
			theme: `tooltipster-default tooltipster-theme-${this.theme}`,
			delay: 400,
			maxWidth: 250,
			side: ['top'],
			animationDuration: 200,
		});

		const tooltipIconWrapperEl = $(
			'<div class="tooltipster-wrapper tooltipster-theme-light"></div>',
		).append(tooltipIconEl);

		// Adding .period_picker_is_rolling to bottom box
		const isRollingWrapperEl = $('<div class="period_picker_is_rolling"></div>').append(
			inputCheckboxWrapperEl,
			tooltipIconWrapperEl,
		);

		bottomBox.addClass('has-rolling-checkbox');
		bottomBox.append(isRollingWrapperEl);
	}

	moveWeekdayNamesRow(pickerEl: PeriodPicker) {
		const weekdaysRow = pickerEl.find('.period_picker_first_letters_tr').eq(0);
		let weekdaysCell = weekdaysRow.find('.period_picker_first_letters_td').eq(0);

		weekdaysCell = $(document.createElement('th'))
			.addClass('period_picker_first_letters_th')
			.attr({ colspan: 8 })
			.html(weekdaysCell.html());

		weekdaysRow.nextAll('tr').each((_k, rowNode) => {
			$(rowNode)
				.find('>td')
				.each((_k, cellNode) => {
					const el = $(cellNode),
						monthTitleRow = el.find('tr').eq(0);
					const weekdaysRow = $(document.createElement('tr')).append(
						weekdaysCell.clone(),
					);
					weekdaysRow.insertAfter(monthTitleRow);
				});
		});

		weekdaysRow.remove();
	}

	updateDateInputs(pickerEl: PeriodPicker) {
		// this is here to fix a period picker bug where the inputs from-to in period picker popup
		// sometimes do not update correctly https://app.asana.com/0/1180537054235286/1199157954866412/f
		// but do NOT "fix" the dates when user is editing them directly (field focused)
		const startDate = this.startValueFormatted,
			endDate = this.endValueFormatted,
			startEl = pickerEl.find('.period_picker_from'),
			endEl = pickerEl.find('.period_picker_to');

		if (!startEl.is(':focus') && startDate !== startEl.val()) {
			startEl.val(startDate);
		}
		if (!endEl.is(':focus') && endDate !== endEl.val()) {
			endEl.val(endDate);
		}
	}

	showErrorMessage(pickerEl: PeriodPicker, message: string) {
		let errorEl = pickerEl.find('.period_picker_error_message');
		if (!errorEl.length) {
			errorEl = $('<div class="period_picker_error_message"></div>');
			pickerEl.find('.period_picker_clear').after(errorEl);
		}
		errorEl.text(message);
	}

	hideErrorMessage(pickerEl: PeriodPicker) {
		pickerEl.find('.period_picker_error_message').remove();
	}

	preparePickerPopupForMobile(picker: PeriodPicker) {
		if (!this.navigationService.isMobileApp) {
			return;
		}

		// same logic as in modal-popup.js:104
		// also offsets are same, except when not in full size (popup is centered)
		if (window.innerWidth >= MD_BREAKPOINT) {
			return;
		}

		const pickerEl = picker[0]!;

		// mobile app works only in portrait mode
		// need to be checked if this works correctly when landscape will be implemented
		if (!this._pickerElInitialHeight) {
			this._pickerElInitialHeight = pickerEl.offsetHeight;
		}

		const isPickerFullSize = pickerEl.classList.contains('period_picker_maximize'),
			fullSizeOffsetTop = 60,
			offsetTop = isPickerFullSize
				? fullSizeOffsetTop
				: (window.innerHeight - this._pickerElInitialHeight) / 2,
			offsetLeft = 15,
			width = window.innerWidth - offsetLeft * 2,
			height = isPickerFullSize
				? window.innerHeight - fullSizeOffsetTop - offsetLeft
				: this._pickerElInitialHeight,
			isImportant = isPickerFullSize ? 'important' : ''; // when resizing from maximized to normal sized picker need to overide properties

		pickerEl.style.setProperty('top', `${offsetTop}px`, isImportant);
		pickerEl.style.setProperty('left', `${offsetLeft}px`, isImportant);
		pickerEl.style.setProperty('width', `${width}px`, isImportant);
		pickerEl.style.setProperty('height', `${height}px`, isImportant);
	}

	@action
	teardownPeriodpicker() {
		if (this.picker) {
			const isRollingWrapperEl = $('.period_picker_submit_dates .period_picker_is_rolling');

			if (isRollingWrapperEl.length) {
				isRollingWrapperEl.find('#isRolling').off('change.isRolling');
			}

			try {
				this.picker.destroy();
			} catch (_e) {
				// skip
			}
		}
	}

	@action
	change() {
		this.args.onChange(this.startValue || '', this.endValue || '', this.isRolling);
	}
	<template>
		<div
			class={{joinClasses
				(array "period-picker" (if @isDisabled "period-picker--disabled") @extraClass)
			}}
			{{didInsert this.setupPeriodpicker}}
			{{didUpdate this.updateLocalValues @startValue @endValue}}
			{{willDestroy this.teardownPeriodpicker}}
		>

			<FormInputComponent
				name={{this.startInputName}}
				class="period-picker-start"
				@value={{this.startValueFormatted}}
			/>

			<FormInputComponent
				name={{this.endInputName}}
				class="period-picker-end"
				@value={{this.endValueFormatted}}
			/>

		</div>
	</template>
}
